import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, ArrowRight, Glasses, Loader2, SearchX, SlidersHorizontal } from 'lucide-react';
import { ApiErrorCode, PERMISSIONS, type FrameRecommendationItem } from '@optiflow/types';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Drawer } from '@/components/ui/Drawer';
import { EmptyState } from '@/components/ui/EmptyState';
import { PageHeader } from '@/components/ui/PageHeader';
import { Pagination } from '@/components/ui/Pagination';
import { Skeleton } from '@/components/ui/Skeleton';
import { useBranch } from '@/hooks/useBranch';
import { useCurrency } from '@/hooks/useCurrency';
import { useLocale } from '@/hooks/useLocale';
import { usePermissions } from '@/hooks/usePermissions';
import { apiErrorCode } from '@/services/apiClient';
import { useCustomerQuery } from '@/modules/customers/api/customers.api';
import { SetFaceShapeDialog } from '@/modules/face/components/SetFaceShapeDialog';
import { OPTICAL_BUILDER_PERMISSIONS } from '@/modules/optical-orders/permissions';
import {
  handoffState,
  type FrameRecommendationHandoff,
} from '@/modules/sales/recommendationHandoff';
import {
  frameRecommendationKeys,
  useFrameRecommendationsQuery,
} from '../api/frameRecommendations.api';
import { AppliedFilterChips } from '../components/AppliedFilterChips';
import { FaceShapeContext } from '../components/FaceShapeContext';
import { RecommendationCard } from '../components/RecommendationCard';
import { RecommendationFiltersForm } from '../components/RecommendationFiltersForm';
import {
  EMPTY_CRITERIA,
  activeFilterCount,
  criteriaFrom,
  draftFrom,
  readApplied,
  withoutGroup,
  writeApplied,
  type AppliedRecommendation,
  type CriteriaDraft,
  type CriteriaErrors,
} from '../criteria';

const ERROR_KEYS: Partial<Record<string, string>> = {
  [ApiErrorCode.PERMISSION_DENIED]: 'frameRecommendations.errors.permission',
  [ApiErrorCode.FORBIDDEN]: 'frameRecommendations.errors.permission',
  [ApiErrorCode.BRANCH_ACCESS_DENIED]: 'frameRecommendations.errors.branch',
  [ApiErrorCode.BRANCH_CONTEXT_MISSING]: 'frameRecommendations.errors.noBranch',
  [ApiErrorCode.VALIDATION_FAILED]: 'frameRecommendations.errors.invalid',
  [ApiErrorCode.NOT_FOUND]: 'frameRecommendations.errors.customer',
};

/**
 * Frame recommendations for one customer: the customer, their confirmed face shape, filters, and the server's ranking.
 *
 * The customer comes from the address, so it is never chosen twice. The applied filters, the branch scope and the page live in this screen's own history entry — a visit to a frame's details and back finds them where they were — and are never saved to the customer. Every result, its order and its stock come from the server; the page only asks for the next page of the same ranking.
 */
export function FrameRecommendationsPage() {
  const { t } = useTranslation();
  const { customerId = '' } = useParams<{ customerId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { can, canAll } = usePermissions();
  const { branches } = useBranch();
  const { isRtl } = useLocale();
  const { currencyCode, parse, toInput } = useCurrency();
  const resultsRef = useRef<HTMLDivElement>(null);

  const applied = useMemo(() => readApplied(location.state), [location.state]);
  const apply = (next: AppliedRecommendation) =>
    void navigate(
      { pathname: location.pathname, search: location.search, hash: location.hash },
      { replace: true, state: writeApplied(location.state, next) },
    );

  const canSeeStock = can(PERMISSIONS.INVENTORY_VIEW);
  const canReadCatalogue = can(PERMISSIONS.CATALOG_VIEW);
  const canRecordFaceShape = can(PERMISSIONS.FACE_ASSESS);
  // Selling a recommended frame takes both grants; reading the catalogue is not needed for it.
  const canAddToSale = can(PERMISSIONS.FRAME_RECOMMEND) && can(PERMISSIONS.SALES_CREATE);
  // An optical order takes everything the builder needs, and the recommendation still needs frame.recommend at checkout.
  const canStartOpticalOrder =
    can(PERMISSIONS.FRAME_RECOMMEND) && canAll(OPTICAL_BUILDER_PERMISSIONS);
  const hasOtherBranches = canSeeStock && branches.length > 1;

  const customer = useCustomerQuery(customerId);
  // Other branches are asked about only by someone who could have switched them on.
  const recommendations = useFrameRecommendationsQuery(customerId, {
    ...applied,
    includeOtherBranches: applied.includeOtherBranches && hasOtherBranches,
  });

  const [isFiltersOpen, setFiltersOpen] = useState(false);
  const [draft, setDraft] = useState<CriteriaDraft>(() => draftFrom(applied.criteria, toInput));
  const [errors, setErrors] = useState<CriteriaErrors>({});
  const [isRecordingFace, setRecordingFace] = useState(false);

  const page = recommendations.data;
  const stockScope = page?.appliedInputs.stockScope ?? null;
  const filterCount = activeFilterCount(applied.criteria);
  const isUpdating = recommendations.isFetching && recommendations.isPlaceholderData;
  // Only the branches this user is authorized for exist here, and the switch appears only while the server is showing this user stock.
  const offerOtherBranches = hasOtherBranches && (page === undefined || stockScope !== null);

  const openFilters = () => {
    setDraft(draftFrom(applied.criteria, toInput));
    setErrors({});
    setFiltersOpen(true);
  };

  const applyDraft = () => {
    const result = criteriaFrom(draft, parse);
    if (result.errors) {
      setErrors(result.errors);
      return;
    }
    setErrors({});
    setFiltersOpen(false);
    apply({ ...applied, criteria: result.criteria, page: 1 });
  };

  const clearFilters = () => apply({ ...applied, criteria: EMPTY_CRITERIA, page: 1 });

  /** One hand-off of a recommended frame: identifiers, the selection the server signed for it and names to show — never a price, stock or score. */
  const handoffFor = (
    item: FrameRecommendationItem,
    selectionToken: string,
  ): FrameRecommendationHandoff => ({
    handoffId: crypto.randomUUID(),
    customerId,
    customerLabel: customer.data?.displayName ?? null,
    productId: item.productId,
    variantId: item.variantId,
    selectionToken,
    productName: item.productName,
    variantName: item.variantName,
    sku: item.sku,
  });

  /** Takes the frame to the counter once, with the selection the server signed for it and nothing the counter should trust beyond that. */
  const addToSale = (item: FrameRecommendationItem) => {
    if (!item.selectionToken) return;
    void navigate('/sales/counter', { state: handoffState(handoffFor(item, item.selectionToken)) });
  };

  /** Opens the optical order builder for this customer with the frame as the first pair's, the same way: in router state, once, never in the address. */
  const startOpticalOrder = (item: FrameRecommendationItem) => {
    if (!item.selectionToken) return;
    void navigate(`/customers/${customerId}/optical-orders/new`, {
      state: handoffState(handoffFor(item, item.selectionToken)),
    });
  };

  const changePage = (next: number) => {
    apply({ ...applied, page: next });
    resultsRef.current?.scrollIntoView({ block: 'start' });
  };

  const BackIcon = isRtl ? ArrowRight : ArrowLeft;
  const subtitle = customer.data
    ? `${customer.data.displayName} · ${customer.data.customerNumber}`
    : undefined;

  return (
    <div className="space-y-5">
      <Link
        to={`/customers/${customerId}`}
        className="text-muted hover:text-ink inline-flex items-center gap-1.5 text-sm"
      >
        <BackIcon className="size-4" aria-hidden />
        {t('frameRecommendations.backToCustomer')}
      </Link>

      <PageHeader title={t('frameRecommendations.title')} subtitle={subtitle} />

      {page ? (
        <FaceShapeContext
          applied={page.appliedInputs}
          canRecordFaceShape={canRecordFaceShape}
          onRecordFaceShape={() => setRecordingFace(true)}
        />
      ) : null}

      <div
        ref={resultsRef}
        className="flex scroll-mt-4 flex-wrap items-center justify-between gap-3"
      >
        <p className="text-ink flex items-center gap-2 text-sm font-medium" aria-live="polite">
          {page ? t('frameRecommendations.toolbar.count', { count: page.totalEligible }) : null}
          {isUpdating ? (
            <span className="text-muted inline-flex items-center gap-1.5 font-normal">
              <Loader2 className="size-4 animate-spin" aria-hidden />
              {t('frameRecommendations.toolbar.updating')}
            </span>
          ) : null}
        </p>

        <div className="flex flex-wrap items-center gap-3">
          {offerOtherBranches ? (
            <label className="text-ink inline-flex min-h-10 cursor-pointer items-center gap-2 text-sm">
              <input
                type="checkbox"
                role="switch"
                checked={applied.includeOtherBranches}
                onChange={(event) =>
                  apply({ ...applied, includeOtherBranches: event.target.checked, page: 1 })
                }
                className="accent-brand-600 size-4"
              />
              {t('frameRecommendations.toolbar.includeOtherBranches')}
            </label>
          ) : null}

          <Button variant="secondary" onClick={openFilters}>
            <SlidersHorizontal className="size-4" aria-hidden />
            {filterCount > 0
              ? t('frameRecommendations.toolbar.filtersWithCount', { count: filterCount })
              : t('frameRecommendations.toolbar.filters')}
          </Button>
        </div>
      </div>

      <AppliedFilterChips
        criteria={applied.criteria}
        canReadBrands={canReadCatalogue}
        onRemove={(group) =>
          apply({ ...applied, criteria: withoutGroup(applied.criteria, group), page: 1 })
        }
        onClearAll={clearFilters}
      />

      {recommendations.isError ? (
        <Alert tone="error">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <span>
              {t(
                ERROR_KEYS[apiErrorCode(recommendations.error) ?? ''] ??
                  'frameRecommendations.errors.load',
              )}
            </span>
            <Button size="sm" variant="secondary" onClick={() => void recommendations.refetch()}>
              {t('frameRecommendations.errors.retry')}
            </Button>
          </div>
        </Alert>
      ) : !page ? (
        <ResultsSkeleton />
      ) : page.totalEligible === 0 ? (
        <Card>
          {filterCount > 0 ? (
            <EmptyState
              icon={SearchX}
              title={t('frameRecommendations.empty.filtered')}
              description={t('frameRecommendations.empty.filteredHint')}
              action={
                <Button variant="secondary" onClick={clearFilters}>
                  {t('frameRecommendations.filters.clear')}
                </Button>
              }
            />
          ) : (
            <EmptyState
              icon={Glasses}
              title={t('frameRecommendations.empty.catalogue')}
              description={t('frameRecommendations.empty.catalogueHint')}
            />
          )}
        </Card>
      ) : (
        <section aria-label={t('frameRecommendations.results')} aria-busy={isUpdating}>
          <ul
            className={`grid gap-4 transition-opacity sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 ${
              isUpdating ? 'opacity-60' : ''
            }`}
          >
            {page.items.map((item) => (
              <li key={item.variantId} className="min-w-0">
                <RecommendationCard
                  item={item}
                  stockScope={stockScope}
                  canViewFrame={canReadCatalogue}
                  onStartOpticalOrder={
                    canStartOpticalOrder && item.selectionToken
                      ? () => startOpticalOrder(item)
                      : undefined
                  }
                  onAddToSale={
                    canAddToSale && item.selectionToken ? () => addToSale(item) : undefined
                  }
                />
              </li>
            ))}
          </ul>
          <Card className="mt-4">
            <Pagination
              pagination={page.pagination}
              onPageChange={changePage}
              isBusy={recommendations.isFetching}
            />
          </Card>
        </section>
      )}

      <Drawer
        isOpen={isFiltersOpen}
        onClose={() => setFiltersOpen(false)}
        title={t('frameRecommendations.filters.title')}
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => {
                setDraft(draftFrom(EMPTY_CRITERIA, toInput));
                setErrors({});
              }}
            >
              {t('frameRecommendations.filters.reset')}
            </Button>
            <Button onClick={applyDraft}>{t('frameRecommendations.filters.apply')}</Button>
          </>
        }
      >
        {isFiltersOpen ? (
          <RecommendationFiltersForm
            draft={draft}
            onChange={setDraft}
            errors={errors}
            currencyCode={currencyCode}
            canChooseBrands={canReadCatalogue}
          />
        ) : null}
      </Drawer>

      {canRecordFaceShape ? (
        <SetFaceShapeDialog
          customerId={customerId}
          isOpen={isRecordingFace}
          onClose={() => setRecordingFace(false)}
          onRecorded={() =>
            void queryClient.invalidateQueries({
              queryKey: frameRecommendationKeys.forCustomer(customerId),
            })
          }
        />
      ) : null}
    </div>
  );
}

function ResultsSkeleton() {
  const { t } = useTranslation();
  return (
    <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4" role="status">
      <span className="sr-only">{t('common.loading')}</span>
      {Array.from({ length: 6 }).map((_, index) => (
        <Card key={index} className="space-y-3 p-4">
          <div className="flex gap-3">
            <Skeleton className="size-14 rounded-lg" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-4 w-40" />
            </div>
          </div>
          <Skeleton className="h-3 w-full" />
          <Skeleton className="h-3 w-2/3" />
          <Skeleton className="h-5 w-28" />
        </Card>
      ))}
    </div>
  );
}
